/* ------------------------------------------------------------------
   PlansPage.js  (v3.0 – modernized UI, matches dashboard theme)
   Greatly Social – subscription page
------------------------------------------------------------------ */

import React, { useEffect, useState, useContext } from 'react'
import { Container, Spinner } from 'react-bootstrap'
import axios from 'axios'
import UpgradeModal from './UpgradeModal'
import { UserContext } from '../../../context/UserContext'

/* ─────── fallback ONLY if API lacks a field (mirrors DB) ─────── */
const FALLBACK_LIMITS = {
  starter : { posts: 15,  ai: 10, aiImages: 8,   accounts:  3, storage: '5 GB'  },
  pro     : { posts: 30,  ai: 20, aiImages: 30,  accounts: 10, storage: '25 GB' },
  agency  : { posts: 100, ai: 30, aiImages: 100, accounts: 20, storage: '100 GB' },
}

const PLAN_DESCRIPTIONS = {
  starter : 'Perfect for individuals getting started with social media.',
  pro     : 'For creators and small businesses ready to grow.',
  agency  : 'Built for teams managing multiple clients at scale.',
}

/* ─────── Feature check row ─────── */
const Feature = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
    <div style={{
      width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
      background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <i className='fas fa-check' style={{ fontSize: 8, color: '#2563eb' }} />
    </div>
    <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
  </div>
)

/* ─────── Stat pill ─────── */
const StatPill = ({ value, label }) => (
  <div style={{
    flex: 1,
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '10px 8px',
    textAlign: 'center',
  }}>
    <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{label}</div>
  </div>
)

/* ─────── Plan Card ─────── */
const PlanCard = ({ plan, isCurrent, isPopular, yearly, priceDisplay, intervalDisplay, onUpgrade }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position     : 'relative',
        background   : '#ffffff',
        border       : `2px solid ${isPopular ? '#2563eb' : hovered ? '#93c5fd' : '#e5e7eb'}`,
        borderRadius : 16,
        padding      : '28px 24px 24px',
        display      : 'flex',
        flexDirection: 'column',
        transition   : 'all 0.2s ease',
        boxShadow    : isPopular
          ? '0 8px 30px rgba(37,99,235,0.15)'
          : hovered
            ? '0 4px 16px rgba(0,0,0,0.08)'
            : '0 1px 4px rgba(0,0,0,0.05)',
        flex         : '1 1 0',
        minWidth     : 260,
        maxWidth     : 340,
      }}
    >
      {/* Popular badge */}
      {isPopular && (
        <div style={{
          position  : 'absolute',
          top       : -13,
          left      : '50%',
          transform : 'translateX(-50%)',
          background: '#2563eb',
          color     : '#fff',
          fontSize  : 11,
          fontWeight: 700,
          padding   : '3px 14px',
          borderRadius: 99,
          letterSpacing: '0.05em',
          whiteSpace: 'nowrap',
        }}>
          ⚡ Most Popular
        </div>
      )}

      {/* Current plan badge */}
      {isCurrent && (
        <div style={{
          position  : 'absolute',
          top       : 16,
          right     : 16,
          background: '#dcfce7',
          color     : '#16a34a',
          fontSize  : 11,
          fontWeight: 700,
          padding   : '3px 10px',
          borderRadius: 99,
        }}>
          ✓ Active
        </div>
      )}

      {/* Plan name */}
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6b7280', marginBottom: 6 }}>
        {plan.name}
      </div>

      <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20, lineHeight: 1.4 }}>
        {PLAN_DESCRIPTIONS[plan.name.toLowerCase()] || ''}
      </div>

      {/* Price */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, marginBottom: 4 }}>
        <span style={{ fontSize: 40, fontWeight: 800, color: '#111827', lineHeight: 1 }}>
          {priceDisplay(plan.price)}
        </span>
      </div>
      <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 24 }}>
        {intervalDisplay}
        {yearly && <span style={{ color: '#16a34a', fontWeight: 600, marginLeft: 6 }}>4 months free</span>}
      </div>

      {/* CTA Button */}
      <button
        disabled={isCurrent}
        onClick={() => onUpgrade(plan.priceId)}
        style={{
          width       : '100%',
          padding     : '11px 0',
          borderRadius: 9,
          border      : isCurrent ? 'none' : isPopular ? 'none' : '1.5px solid #2563eb',
          background  : isCurrent
            ? '#f3f4f6'
            : isPopular
              ? '#2563eb'
              : 'transparent',
          color       : isCurrent ? '#9ca3af' : isPopular ? '#fff' : '#2563eb',
          fontSize    : 14,
          fontWeight  : 700,
          cursor      : isCurrent ? 'not-allowed' : 'pointer',
          marginBottom: 24,
          transition  : 'all 0.15s',
        }}
        onMouseEnter={e => {
          if (!isCurrent && isPopular) e.currentTarget.style.background = '#1d4ed8'
          if (!isCurrent && !isPopular) { e.currentTarget.style.background = '#eff6ff' }
        }}
        onMouseLeave={e => {
          if (!isCurrent && isPopular) e.currentTarget.style.background = '#2563eb'
          if (!isCurrent && !isPopular) { e.currentTarget.style.background = 'transparent' }
        }}
      >
        {isCurrent ? '✓ Current Plan' : 'Upgrade to ' + plan.name.charAt(0).toUpperCase() + plan.name.slice(1)}
      </button>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <StatPill value={plan.posts?.toLocaleString()} label='Posts/mo' />
        <StatPill value={plan.ai?.toLocaleString()} label='AI Credits' />
        <StatPill value={plan.accounts} label='Accounts' />
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: '#f3f4f6', marginBottom: 20 }} />

      {/* Features */}
      <div style={{ flex: 1 }}>
        {plan.aiImages != null && <Feature label={`${plan.aiImages} bulk posts / month`} />}
        <Feature label='AI Post Creator' />
        <Feature label='AI Caption Generator' />
        <Feature label='Video Editor' />
        <Feature label={`File Manager (${plan.storage})`} />
        <Feature label='Content Calendar' />
        {['pro', 'agency'].includes(String(plan.name).toLowerCase()) &&
          <Feature label='Analytics & Insights' />}
        {['pro', 'agency'].includes(String(plan.name).toLowerCase()) &&
          <Feature label='No Greatly Social branding' />}
      </div>

      {/* Social platform icons */}
      <div style={{
        display    : 'flex',
        gap        : 10,
        marginTop  : 20,
        paddingTop : 16,
        borderTop  : '1px solid #f3f4f6',
        flexWrap   : 'wrap',
        alignItems : 'center',
      }}>
        {['facebook-f','instagram','x-twitter','linkedin-in','pinterest','tiktok','youtube'].map(icon => (
          <i
            key={icon}
            className={`fab fa-${icon}`}
            style={{ fontSize: 13, color: '#9ca3af' }}
          />
        ))}
      </div>
    </div>
  )
}

/* ─────── Main Page ─────── */
export default function PlansPage () {
  const { user, setUser } = useContext(UserContext)

  const [plans, setPlans]             = useState([])
  const [loading, setLoading]         = useState(true)
  const [yearly, setYearly]           = useState(false)
  const [selectedPriceId, setSelectedPriceId] = useState(null)
  const [showModal, setShowModal]     = useState(false)

  useEffect(() => { fetchPlans() }, [])

  async function fetchPlans () {
    try {
      const { data } = await axios.get('/api/billing/stripe/plans', { withCredentials: true })
      const allowed  = ['starter', 'pro', 'agency']
      const mapped   = data
        .filter(p => allowed.includes(p.name.toLowerCase()))
        .map(p => {
          const fb = FALLBACK_LIMITS[p.name.toLowerCase()] || {}
          return {
            ...p,
            posts   : p.post_quota     ?? fb.posts,
            ai      : p.ai_quota       ?? fb.ai,
            aiImages: p.ai_image_quota ?? fb.aiImages,
            accounts: p.account_limit  ?? fb.accounts,
            storage : p.storage        ?? fb.storage,
          }
        })
      setPlans(mapped)
    } catch (err) {
      console.error('Plans fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  const priceDisplay    = cents =>
    yearly ? `$${((cents * 8) / 100).toLocaleString()}` : `$${(cents / 100).toLocaleString()}`
  const intervalDisplay = yearly ? 'USD / year' : 'USD / month'

  const handleUpgrade    = id  => { setSelectedPriceId(id); setShowModal(true) }
  const handleSubscribed = (planKey, renewalDate) =>
    setUser({ ...user, plan: planKey, renewalDate })

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
          Plans & Billing
        </h2>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
          Choose the plan that fits your workflow. Upgrade or downgrade any time.
        </p>
      </div>

      {/* Billing toggle */}
      <div style={{
        display     : 'inline-flex',
        alignItems  : 'center',
        gap         : 0,
        background  : '#f3f4f6',
        borderRadius: 10,
        padding     : 4,
        marginBottom: 36,
      }}>
        {['Monthly', 'Yearly'].map(opt => {
          const isActive = (opt === 'Yearly') === yearly
          return (
            <button
              key={opt}
              onClick={() => setYearly(opt === 'Yearly')}
              style={{
                padding     : '8px 20px',
                borderRadius: 7,
                border      : 'none',
                background  : isActive ? '#ffffff' : 'transparent',
                color       : isActive ? '#111827' : '#6b7280',
                fontWeight  : isActive ? 700 : 500,
                fontSize    : 13,
                cursor      : 'pointer',
                boxShadow   : isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition  : 'all 0.15s',
                position    : 'relative',
              }}
            >
              {opt}
              {opt === 'Yearly' && (
                <span style={{
                  position  : 'absolute',
                  top       : -10,
                  right     : -4,
                  background: '#dcfce7',
                  color     : '#16a34a',
                  fontSize  : 9,
                  fontWeight: 700,
                  padding   : '1px 6px',
                  borderRadius: 99,
                  whiteSpace: 'nowrap',
                }}>
                  SAVE 33%
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Plan cards */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Spinner animation='border' style={{ color: '#2563eb' }} />
        </div>
      ) : (
        <div style={{
          display  : 'flex',
          gap      : 20,
          flexWrap : 'wrap',
          alignItems: 'stretch',
        }}>
          {plans.map(p => (
            <PlanCard
              key={p.priceId}
              plan={p}
              isCurrent={user?.plan === p.name}
              isPopular={!!p.pop}
              yearly={yearly}
              priceDisplay={priceDisplay}
              intervalDisplay={intervalDisplay}
              onUpgrade={handleUpgrade}
            />
          ))}
        </div>
      )}

      {/* Footer note */}
      {!loading && (
        <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 28, textAlign: 'center' }}>
          * Prices shown exclude applicable taxes. Cancel or change your plan at any time from your billing settings.
        </p>
      )}

      <UpgradeModal
        show={showModal}
        priceId={selectedPriceId}
        onHide={() => setShowModal(false)}
        onSubscribed={handleSubscribed}
      />
    </div>
  )
}